import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon } from "lucide-react";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getGetTemplateListQueryKey,
  useCreateTemplate,
} from "~/api/custom-reports/custom-reports";
import { useListOrganizationCategories } from "~/api/organization/organization";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import { useToast } from "~/hooks/use-toast";
import { DraggableSections } from "./DraggableSections";
import { TemplateSection } from "./helpers";

export enum CreateTemplateStep {
  NAME_AND_DEFINITION = 1,
  INSTRUCTIONS = 2,
}

interface CreateTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
}

interface FormData {
  name: string;
  definition: string;
  sections: TemplateSection[];
}

interface TemplateObject {
  name: string;
  definition: string;
  templateSections: TemplateSection[];
}

export function CreateTemplateModal({
  open,
  onOpenChange,
  organizationId,
}: CreateTemplateModalProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const createTemplate = useCreateTemplate({
    mutation: {
      onSuccess: () => {
        // Invalidate and refetch template list after successful creation
        queryClient.invalidateQueries({
          queryKey: getGetTemplateListQueryKey(organizationId),
        });
      },
      onError: () => {
        toast({
          title: intl.formatMessage({
            id: "createTemplate.error",
            defaultMessage: "Failed to create template",
          }),
        });
      },
    },
  });
  const [step, setStep] = useState<CreateTemplateStep>(
    CreateTemplateStep.NAME_AND_DEFINITION
  );
  const [formData, setFormData] = useState<FormData>({
    name: "",
    definition: "",
    sections: [
      { title: "", purpose: "", prompt: "", order: 0, sectionType: "snapshot" },
    ],
  });
  const [showError, setShowError] = useState(false);
  const [parsingError, setParsingError] = useState(false);

  const { data: categoriesData } = useListOrganizationCategories(
    organizationId,
    {
      query: {
        enabled: open,
      },
    }
  );

  const availableCategories = React.useMemo(() => {
    if (!categoriesData) return [];
    return categoriesData.map((cat) => ({
      id: cat.id,
      name: cat.name,
      color: cat.color,
    }));
  }, [categoriesData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === CreateTemplateStep.NAME_AND_DEFINITION) {
      if (!formData.name.trim() || !formData.definition.trim()) {
        setShowError(true);
        return;
      }
      setShowError(false);
      setStep(CreateTemplateStep.INSTRUCTIONS);
    } else {
      if (formData.sections.length === 0) {
        setShowError(true);
        return;
      }

      const hasEmptyFields = formData.sections.some((section) => {
        if (section.sectionType === "docList") {
          return !section.title;
        }
        return !section.title || !section.purpose || !section.prompt;
      });

      if (hasEmptyFields) {
        setParsingError(true);
        return;
      }

      const templateObject: TemplateObject = {
        name: formData.name,
        definition: formData.definition,
        templateSections: formData.sections,
      };

      createTemplate.mutate(
        {
          organizationId,
          data: {
            name: templateObject.name,
            definition: templateObject.definition,
            sections: templateObject.templateSections.map(
              ({ title, purpose, prompt, sectionType, config }) => ({
                title,
                purpose:
                  sectionType === "docList"
                    ? "List all document titles by category"
                    : purpose,
                prompt: sectionType === "docList" ? "docList" : prompt,
                contextType: sectionType === "docList" ? "docList" : "snapshot",
                config: config
                  ? {
                      excludedCategoryNames:
                        config.excludedCategoryNames?.map(String),
                    }
                  : undefined,
              })
            ),
          },
        },
        {
          onSuccess: () => {
            // Close modal and reset form only after successful creation
            onOpenChange(false);
            setStep(CreateTemplateStep.NAME_AND_DEFINITION);
            setFormData({
              name: "",
              definition: "",
              sections: [
                {
                  title: "",
                  purpose: "",
                  prompt: "",
                  order: 0,
                  sectionType: "snapshot",
                },
              ],
            });
            setShowError(false);
            setParsingError(false);
          },
        }
      );
    }
  };

  const handleBack = () => {
    setStep(CreateTemplateStep.NAME_AND_DEFINITION);
    setShowError(false);
    setParsingError(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep(CreateTemplateStep.NAME_AND_DEFINITION);
    setFormData({
      name: "",
      definition: "",
      sections: [
        {
          title: "",
          purpose: "",
          prompt: "",
          order: 0,
          sectionType: "snapshot",
        },
      ],
    });
    setShowError(false);
    setParsingError(false);
  };

  const renderStepContent = () => {
    if (step === CreateTemplateStep.NAME_AND_DEFINITION) {
      return (
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="name">
              <FormattedMessage
                id="template.templateName"
                defaultMessage="Template name"
              />
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={intl.formatMessage({
                id: "template.placeholders.name",
              })}
            />
            {!formData.name && showError && (
              <p className="text-sm text-destructive">
                <FormattedMessage
                  id="template.nameRequired"
                  defaultMessage="The name field can't be empty"
                />
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="definition">
              <FormattedMessage
                id="template.definition"
                defaultMessage="Definition"
              />
            </Label>
            <AutoResizeTextarea
              id="definition"
              value={formData.definition}
              onChange={(e) =>
                setFormData({ ...formData, definition: e.target.value })
              }
              placeholder={intl.formatMessage({
                id: "template.placeholders.definition",
              })}
            />
            {!formData.definition && showError && (
              <p className="text-sm text-destructive">
                <FormattedMessage
                  id="template.definitionRequired"
                  defaultMessage="The definition field can't be empty"
                />
              </p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <DraggableSections
          sections={formData.sections}
          onChange={(sections) => {
            setFormData({ ...formData, sections });
            setShowError(false);
            setParsingError(false);
          }}
          showError={showError}
          parsingError={parsingError}
          availableCategories={availableCategories}
        />
      </div>
    );
  };

  const renderFooter = () => {
    if (step === CreateTemplateStep.NAME_AND_DEFINITION) {
      return (
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={handleClose}>
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" form="create-template-form">
            <FormattedMessage
              id="template.continue"
              defaultMessage="Continue"
            />
          </Button>
        </div>
      );
    }

    return (
      <>
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          className="sm:mr-auto"
        >
          <ChevronLeftIcon className="w-4 h-4 mr-2" />
          {intl.formatMessage({ id: "template.back" })}
        </Button>
        <Button type="button" variant="outline" onClick={handleClose}>
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="submit" form="create-template-form">
          <FormattedMessage
            id="createTemplate.create"
            defaultMessage="Create Template"
          />
        </Button>
      </>
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <FormattedMessage
          id="createTemplate.title"
          defaultMessage="Create Report Template"
        />
      }
      description={
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            <FormattedMessage
              id="template.step"
              defaultMessage="Step {step} of 2"
              values={{ step }}
            />
          </p>
          <p className="text-sm text-muted-foreground">
            {step === CreateTemplateStep.NAME_AND_DEFINITION ? (
              <FormattedMessage
                id="createTemplate.step1Description"
                defaultMessage="Add name and definition"
              />
            ) : (
              <FormattedMessage
                id="createTemplate.step2Description"
                defaultMessage="Describe the instructions for the template"
              />
            )}
          </p>
        </div>
      }
      content={
        <form
          onSubmit={handleSubmit}
          id="create-template-form"
          className="space-y-6"
        >
          {renderStepContent()}
        </form>
      }
      footer={renderFooter()}
    />
  );
}
